import React from "react";
import * as style from "../styles.module.scss";
import OnboardImage from "../../OnboardImage";
import {
  PersonalDetails,
  TypeOfWork,
  CV,
  WorkHistory,
  EmergencyContact,
  Demographics,
  ProfilePicture,
  PersonalityTest,
} from "../index";

const RegistrationStep = ({
  demographicsState,
  demographicsErrors,
  handleDemographicsState,
  handleSubmitDemographics,
  demographicsSelectOptions,
  emergencyContactState,
  emergencyContactErrors,
  handleEmergencyContactState,
  handleSubmitEmergencyContact,
  cvUploaded,
  fileValidationError,
  workHistoryState,
  workHistoryErrors,
  handleWorkHistoryState,
  handleSubmitWorkHistory,
  handleAddNewWorkHistory,
  handleRemoveWorkHistory,
  personalDetailsActions,
  personalDetailsStateHandlers,
  matchedAddressList,
  responsibleTeamCode,
  onBranchChange,
  postcodeError,
  branches,
  personalDetailsState,
  setPersonalDetailsState,
  personalDetailsErrors,
  handleSubmitCV,
  handleProfilePictureSubmitted,
  handleCvSubmitted,
  handleSubmitTypeOfWork,
  typeOfWorkState,
  handleTypeWork,
  typeOfWorkErrors,
  cvLoading,
  section,
  findStep,
  jobId,
  appData,
  currentJobProgressState,
  handlePersonalityTestCompleted,
  personalityTestState,
}) => {
  const demographicOptions = demographicsSelectOptions(appData);
  const components = {
    "personal-details": {
      component: (
        <PersonalDetails
          {...personalDetailsActions}
          {...personalDetailsStateHandlers}
          responsibleTeamCode={responsibleTeamCode}
          onBranchChange={onBranchChange}
          postcodeError={postcodeError}
          branches={branches}
          navigateBackPath={"/profile"}
          personalDetailsState={personalDetailsState}
          setPersonalDetailsState={setPersonalDetailsState}
          personalDetailsErrors={personalDetailsErrors}
          matchedAddressList={matchedAddressList}
        />
      ),
      title: "Personal details",
      description:
        "Please check your personal details and ensure they are all correct. This information is needed to process your application smoothly.",
      additionalDescription: null,
      imagePath: "/images/my_location.svg",
      imageClass: "location__image",
      currentStep: 1,
    },
    "profile-picture": {
      component: (
        <ProfilePicture
          appData={appData}
          handleProfilePictureSubmitted={handleProfilePictureSubmitted}
          navigateBackPath={findStep("default", jobId)}
        />
      ),
      title: "Profile picture",
      description: "Please add your profile picture.",
      additionalDescription: null,
      imagePath: "/images/my_profilepicture.svg",
      imageClass: "profilepicture__image",
      currentStep: 2,
    },
    "type-of-work": {
      component: (
        <TypeOfWork
          handleSubmit={handleSubmitTypeOfWork}
          state={typeOfWorkState}
          handleBack={findStep("personal-details", jobId)}
          handleChange={handleTypeWork}
          errors={typeOfWorkErrors}
        />
      ),
      title: "Type of work",
      description: "What type of work are you looking for?",
      additionalDescription:
        "This information helps us ensure you are a great fit and can match you to relevant jobs in future.",

      imagePath: "/images/onboard_work_type.svg",
      imageClass: "workType__image",
      currentStep: 3,
    },
    cv: {
      component: (
        <CV
          handleSubmitCV={handleSubmitCV}
          handleCvSubmitted={handleCvSubmitted}
          cvUploaded={cvUploaded}
          fileValidationError={fileValidationError}
          loading={cvLoading}
          navigateBackPath={findStep("profile-picture", jobId)}
          workHistoryPath={`/profile/edit/job/${jobId}/registration/work-history`}
        />
      ),
      title: "CV/work history",
      description:
        "Please add your CV or work history. This is required for all job applications.",
      additionalDescription: null,
      imagePath: "/images/cv_info.svg",
      imageClass: "cvOrWorkHistory__image",
      currentStep: 4,
    },
    "work-history": {
      component: (
        <WorkHistory
          workHistoryState={workHistoryState}
          workHistoryErrors={workHistoryErrors}
          handleWorkHistoryState={handleWorkHistoryState}
          handleSubmitWorkHistory={handleSubmitWorkHistory}
          handleAddNewWorkHistory={handleAddNewWorkHistory}
          handleRemoveWorkHistory={handleRemoveWorkHistory}
          navigateBackPath={findStep("work-history-back", jobId, appData)}
          linkToCV={findStep("always-cv", jobId)}
        />
      ),
      title: "CV/work history",
      description:
        "Please add your CV or work history. This is required for all job applications.",
      additionalDescription: null,
      imagePath: "/images/cv_info.svg",
      imageClass: "cvOrWorkHistory__image",
      currentStep: 4,
    },
    "emergency-contact": {
      component: (
        <EmergencyContact
          state={emergencyContactState}
          errors={emergencyContactErrors}
          handleState={handleEmergencyContactState}
          handleSubmit={handleSubmitEmergencyContact}
          navigateBackPath={findStep("type-of-work", jobId, appData)}
        />
      ),
      title: "Emergency contact",
      description: "Please let us know your emergency contact's details.",
      additionalDescription: null,
      imagePath: "/images/emergency_contact.svg",
      imageClass: "emergencyContact__image",
      currentStep: 5,
    },
    demographics: {
      component: (
        <Demographics
          state={demographicsState}
          errors={demographicsErrors}
          handleState={handleDemographicsState}
          handleSubmit={handleSubmitDemographics}
          navigateBackPath={findStep("cv", jobId, appData)}
          {...demographicOptions}
        />
      ),
      title: "Demographics",
      description:
        "We want to make sure our hiring process is inclusive and fair. None of this information will be shared with clients or teams making hiring decisions - we use this solely for the purpose for ensuring equality, diversity and inclusion.",
      additionalDescription:
        "All of your responses will be kept confidential and used to improve the work we do here at HRGO Recruitment.",
      imagePath: "/images/demographics.svg",
      imageClass: "demographics__image",
      currentStep: 6,
    },
    "personality-test": {
      component: (
        <PersonalityTest
          navigateBackPath={findStep("emergency-contact", jobId, appData)}
          handlePersonalityTestCompleted={handlePersonalityTestCompleted}
          personalityTestState={personalityTestState}
        />
      ),
      title: "Personality test",
      description:
        "Gain insights into your personality! This can help you make informed career decisions and help us to help you find a work environment where you can do your best work.",
      additionalDescription: null,
      imagePath: "/images/personality_test.svg",
      imageClass: "personalityTest__image",
      currentStep: 7,
    },
  };

  return (
    <div className="row h-100">
      <OnboardImage
        title={components[section].title}
        desktopDescription={components[section].description}
        mobileDescription={components[section].description}
        additionalDescription={components[section].additionalDescription}
        imagePath={components[section].imagePath}
        className={style.imageBackground}
        imageClassName={style[components[section].imageClass]}
        innerProgressShow
        innerProgressCurrentStep={components[section].currentStep}
        innerProgressTotalSteps={7}
        innerProgressTitle="Registration"
        currentJobProgressState={currentJobProgressState}
      />
      {components[section].component}
    </div>
  );
};

export default RegistrationStep;
